import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import SiteHeader from "@/components/shop/SiteHeader";
import SiteFooter from "@/components/shop/SiteFooter";
import CartDrawer from "@/components/shop/CartDrawer";
import ProductCard from "@/components/shop/ProductCard";
import { api } from "@/lib/api";

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const [data, setData] = useState({ categories: [], products: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/products").then(({ data }) => {
      setData(data);
      setLoading(false);
    });
  }, []);

  const filtered = category === "all"
    ? data.products
    : data.products.filter((p) => p.category === category);

  const setCategory = (slug) => {
    if (slug === "all") setSearchParams({});
    else setSearchParams({ category: slug });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="shop-page">
      <SiteHeader />
      <CartDrawer />

      {/* Hero */}
      <section className="border-b border-slate-100 bg-gradient-to-b from-sky-50/60 to-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 md:py-20">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.25em] text-amber-500 font-bold"
          >
            The Morning Soft Shop
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-[#0f2a5c] max-w-3xl leading-[1.02]"
          >
            Everyday essentials, <em className="text-amber-500 not-italic">softer</em>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-base text-slate-600 max-w-2xl"
          >
            Bathroom tissue, kitchen towels, wipes and personal care — thoughtfully designed for the American home.
          </motion.p>
        </div>
      </section>

      {/* Category tabs */}
      <div className="sticky top-16 z-20 bg-white/85 backdrop-blur-md border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex gap-2 overflow-x-auto" data-testid="category-tabs">
          {[{ slug: "all", name: "All" }, ...data.categories].map((c) => (
            <button
              key={c.slug}
              data-testid={`category-tab-${c.slug}`}
              onClick={() => setCategory(c.slug)}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                category === c.slug
                  ? "bg-[#173a7a] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
        {loading ? (
          <div className="py-20 text-center text-slate-400" data-testid="shop-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-slate-400" data-testid="shop-empty">
            No products found in this category.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="products-grid">
            {filtered.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        )}
        <div className="mt-14 text-center">
          <Link to="/" className="text-sm text-slate-500 underline underline-offset-4 hover:text-[#173a7a]">
            ← Back to the story
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
